import type { SimulatorArea } from '@vedalib/editor/lib/elements'
import { useCallback, useState } from 'react'

import Button, { IconButton } from 'components/uiKit/Button'
import Dropdown from 'components/uiKit/Dropdown'
import Tooltip from 'components/uiKit/Dropdown/Tooltip'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import AreaDropdown from '../AreaDropdown'
import AreaSettings from '../AreaSettings'
import { useSimulatorContext } from '../SimulatorContext'
import ToolbarLayout from '../ToolbarLayout'
import { getAreaHeightInPixels } from '../helper'
import * as s from './AreaToolbar.module.scss'

interface IAreaToolbarProps {
  area: SimulatorArea
  currentItemId: string
  onDeleteArea: () => void
  itemId?: string
  isVisibleItems: boolean
  setIsVisibleItems: React.Dispatch<React.SetStateAction<boolean>>
}

const AreaToolbar = ({
  area,
  itemId,
  currentItemId,
  onDeleteArea,
  isVisibleItems,
  setIsVisibleItems,
}: IAreaToolbarProps) => {
  const styles = useSimulatorContext((c) => c.styles)
  const value = useSimulatorContext((c) => c.value)
  const setState = useSimulatorContext((c) => c.setState)
  const onAddNewItem = useSimulatorContext((c) => c.onAddNewItem)
  const onLinkArea = useSimulatorContext((c) => c.onLinkArea)
  const [showAreaSettings, setShowAreaSettings] = useState(false)
  const areaHeightInPixel = getAreaHeightInPixels(area.height, styles.shell.minHeight || 0)
  const goToNextItem = useCallback(
    () => setState?.({ active: area.itemId || null }),
    [area.itemId, setState],
  )

  const onSelectItem = useCallback((screenItemId: string) => onLinkArea(screenItemId), [onLinkArea])

  return (
    <ToolbarLayout>
      <div className={s.buttonsGap}>
        <AreaDropdown
          areaHeight={areaHeightInPixel}
          currentItemId={currentItemId}
          isVisibleItems={isVisibleItems}
          itemId={itemId}
          items={value.items}
          onAddNewItem={onAddNewItem}
          onSelectItem={onSelectItem}
          setIsVisibleItems={setIsVisibleItems}
        />
        <Dropdown
          offset={[10, 0]}
          onVisibleChange={setShowAreaSettings}
          overlay={<AreaSettings area={area} />}
          placement='bottom-start'
          styleType='clear'
          visible={showAreaSettings}
        >
          {() => (
            <Tooltip
              overlay={t('elements.simulator.tooltip.areaSettings')}
              trigger={['hoverWithoutContent']}
            >
              <Button
                active={showAreaSettings}
                name='areaSettings'
                size={KitSize.S}
                styleType='secondary'
              >
                {t('uiKit.button.settings')}
              </Button>
            </Tooltip>
          )}
        </Dropdown>
        {itemId && (
          <Tooltip overlay={t('elements.simulator.tooltip.goTo')}>
            <IconButton
              icon='arrowMoreOpenNewTab'
              iconSize={KitSize.S}
              name='linkedItem'
              onClick={goToNextItem}
              size={KitSize.S}
              styleType='ghost'
            />
          </Tooltip>
        )}
      </div>
      <Tooltip overlay={t('elements.simulator.tooltip.areaDelete')}>
        <IconButton
          icon='trashDeleteBin'
          iconSize={KitSize.S}
          name='deleteArea'
          onClick={onDeleteArea}
          size={KitSize.S}
          styleType='ghost'
        />
      </Tooltip>
    </ToolbarLayout>
  )
}

export default AreaToolbar
